<template>
  <v-card-text>
    <div>
      <v-text-field
        v-model="settings.sourceSettings.geoserverUrl"
        label="Адрес WFS сервера"
        hide-details
      />
      <small>
        Для получения данных посредством `GetFeature` из wfs укажите путь с данным параметром.
      </small>
      <small class="d-block">
        Пример:
        <span class="green--text">https://geomonitoring.ru:13161/geoserver/workspace1/wfs?</span><span class="red--text">service=wfs</span>
      </small>

      <div class="d-flex mb-5">
        <v-btn
          class="mr-5"
          x-small
          @click="resetLayer"
        >
          Сбросить слои
        </v-btn>
        <v-btn
          v-if="!layers.length"
          :disabled="!settings.sourceSettings.geoserverUrl || isLoadingGeoserverInfo"
          :loading="isLoadingGeoserverInfo"
          x-small
          @click="getGeoserverInfo"
        >
          Получить список слоёв
        </v-btn>
      </div>

      <custom-select
        v-if="layers.length"
        :list="filteredLayers"
        :data="settings.sourceSettings.layer"
        item-value="value"
        item-text="text"
        label="Наименование слоя"
        search
        search-label="Поиск слоя"
        hide-details
        @search="debouncedLayerSearch"
        @change="onLayerChange"
      />
      <v-text-field
        v-else
        v-model="settings.sourceSettings.layer"
        label="Наименование слоя"
        :disabled="!settings.sourceSettings.geoserverUrl"
        hide-details
      />
      <v-checkbox
        v-model="settings.sourceSettings.supportsJsonOutput"
        label="WFS сервер поддерживает формат GeoJSON"
        hide-details
      />
      <v-checkbox
        v-model="settings.sourceSettings.supportsCql"
        label="WFS сервер поддерживает фильтр CQL"
      />
      <template v-if="settings.sourceSettings.supportsCql">
        <v-text-field
          v-model="settings.sourceSettings.geometryAttributeName"
          label="Наименование аттрибута в котором содержится геометрия"
          :disabled="!settings.sourceSettings.geoserverUrl"
        />
      </template>
      <custom-select
        :list="srsList"
        :data="settings.sourceSettings.srs"
        item-value="srs"
        item-text="text"
        label="Формат геометрии"
        @change="$emit('srs-change', $event)"
      />
    </div>
    <v-btn
      class="mr-5"
      x-small
      :disabled="!(settings.sourceSettings.layer && settings.sourceSettings.geoserverUrl)"
      @click="getLayerAttributes"
    >
      Получить список аттрибутов
    </v-btn>

    <v-list v-if="layerAttributes.length" flat>
      <v-subheader>Список аттрибутов (Тип данных)</v-subheader>
      <v-list-item
        v-for="(attribute, i) in layerAttributes"
        :key="i"
        dense
      >
        {{ attribute }}
      </v-list-item>
    </v-list>


    <br>
    <br>
    <v-divider />
    <br>

    <v-range-slider
      v-model="settings.sourceSettings.zoomRange"
      label="Масштаб при котором скачиваются данные с wfs"
      step="1"
      min="1"
      max="22"
      thumb-label="always"
      thumb-size="20"
    />

    <div class="d-flex align-center">
      <v-checkbox
        v-model="settings.timelineType"
        label="Привязка к таймлайну"
      />
      <div class="ml-5">
        Для корректной работы необходимо добавить к адресу wfs фильтр CQL. Например:
        <span class="green--text">
          cql_filter=dt={date}
        </span>
        или
        <span class="green--text">
          cql_filter=date&gt;={start-date-iso} AND date&lt;={end-date-iso}
        </span>
      </div>
    </div>
  </v-card-text>
</template>

<script>
import http from '@/utils/api';
import CustomSelect from '@/components/ui/custom-select';
import { isObject } from '@/utils/object';
import debounce from 'lodash/debounce';
import * as types from '@/constants/layer-types';
import { mapActions } from 'vuex';

export default {
  components: {
    CustomSelect,
  },

  props: {
    settings: {
      type: Object,
      required: true,
    },

    layerAttributes: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      isLoadingGeoserverInfo: false,
      layers: [],
      searchQuery: '',
      srsList: types.SRS_LIST,
    };
  },

  computed: {
    filteredLayers() {
      return (this.layers || [])
        .flatMap((l) => {
          if (l.value === this.settings.sourceSettings.layer) return l;

          return l.text.toLowerCase().includes(this.searchQuery.toLowerCase()) ? l : [];
        });
    },
  },

  async created() {
    this.debouncedLayerSearch = debounce(this.onLayerSearch, 300);
    if (this.settings.sourceSettings.geoserverUrl) {
      await this.getGeoserverInfo();
    }
  },

  methods: {
    ...mapActions(['showNotify']),

    resetLayer() {
      this.layers = [];
      this.settings.sourceSettings.layer = '';
      this.setLayerAttributes([]);
    },

    onLayerSearch(query) {
      this.searchQuery = query;
    },

    onLayerChange(layer) {
      this.setLayerAttributes([]);
      this.settings.sourceSettings.layer = layer;
    },

    async getLayerAttributes() {
      try {
        const response = await http.get('admin/wfs/layer-attributes', {
          params: {
            url: this.settings.sourceSettings.geoserverUrl,
            layer: this.settings.sourceSettings.layer,
          },
        });

        this.setLayerAttributes(response || []);
      } catch (e) {
        this.setLayerAttributes([]);
        this.showNotify({
          type: 'error',
          message: e.message,
        });
        console.error(e);
      }
    },

    setLayerAttributes(attributes) {
      this.$emit('set-layer-attributes', attributes);
    },

    async getGeoserverInfo() {
      this.isLoadingGeoserverInfo = true;
      try {
        const layersRawList = await http.get(
          'admin/wms/info',
          {
            params: {
              geoserver: this.settings.sourceSettings.geoserverUrl,
            },
          },
        );

        let layersList = [];
        if (isObject(layersRawList)) {
        // https://vuetifyjs.com/en/api/v-select/#props
          Object.keys(layersRawList).forEach((k) => {
            if (typeof layersRawList[k] !== 'string') {
              return;
            }
            layersList.push({
              text: layersRawList[k],
              value: k,
            });
          });
        } else {
          layersList = layersRawList;
        }

        this.layers = layersList;
      } catch (e) {
        console.error(e.message);
        this.showNotify({
          type: 'error',
          message: 'Ошибка при получении списка слоёв',
        });
        this.layers = [];
      } finally {
        this.isLoadingGeoserverInfo = false;
      }
    },

    onSrsChange(srs) {
      this.settings.sourceSettings.srs = srs;
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
